/**
 * Notification chime — Phase 6 / M5.2.
 *
 * A COPY of libs/inbox-ui/src/chime.ts, and deliberately so. This app is being
 * replaced by apps/widget-ng; importing the shared library here pulled Angular
 * into this bundle and took it from 200 kB past 500. The copy dies with the
 * app. Its tests moved to the library with the original.
 *
 * Plays a brief sine-pulse via Web Audio when a new INBOUND message
 * lands on a thread the operator isn't currently viewing. Pure helper
 * — gating + preference checking live at the call site (App.svelte).
 *
 * Implementation notes:
 *
 * - We use Web Audio rather than bundling an audio file. A windowed
 *   sine pulse at 800 Hz, ~140 ms total with a fast attack and
 *   exponential decay, is functionally indistinguishable from a
 *   system bleep at this fidelity. Bundling an mp3 would add 10-20
 *   KB and a licensing question for no UX gain.
 *
 * - The AudioContext is constructed lazily and cached on the module.
 *   Most browsers suspend a freshly-constructed context until the
 *   first user gesture (autoplay policy); `ensureRunning()` calls
 *   `resume()` opportunistically. If the context is still suspended
 *   when we try to play (e.g. operator loaded the widget and stayed
 *   idle), the play silently no-ops — the next message after any
 *   click chimes normally.
 *
 * - SSR-safe: every entry point guards on `typeof window` so import
 *   for build/SSR doesn't blow up. Returns false from `playChime` when
 *   audio is unavailable rather than throwing — call sites never
 *   need a try/catch.
 */

let ctx: AudioContext | null = null;
let resolveCtx: (() => AudioContext | null) | null = null;

interface AudioWindow extends Window {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
}

function getContext(): AudioContext | null {
  if (typeof window === 'undefined') return null;
  if (ctx) return ctx;
  if (resolveCtx === null) {
    resolveCtx = () => {
      const w = window as AudioWindow;
      const Ctor = w.AudioContext ?? w.webkitAudioContext;
      if (!Ctor) return null;
      try {
        return new Ctor();
      } catch {
        return null;
      }
    };
  }
  ctx = resolveCtx();
  return ctx;
}

async function ensureRunning(c: AudioContext): Promise<boolean> {
  if (c.state === 'running') return true;
  if (c.state === 'suspended') {
    try {
      await c.resume();
      // TS narrows c.state to 'suspended' after the if-check above; cast
      // through unknown to re-read the live value, which resume() may
      // have flipped to 'running'. Safari's autoplay-policy lock leaves
      // it stuck on 'suspended' even after resume resolves, hence the
      // dynamic re-read instead of an optimistic `return true`.
      return (c.state as string) === 'running';
    } catch {
      return false;
    }
  }
  // 'closed' — unrecoverable; caller will get false.
  return false;
}

export interface ChimeOptions {
  /** Carrier frequency in Hz. Default 800. */
  frequency?: number;
  /** Total envelope length in seconds. Default 0.14. */
  durationSec?: number;
  /** Peak gain (0..1). Default 0.22 — quiet enough not to startle. */
  peakGain?: number;
}

/**
 * Play a single chime. Returns `true` when the tone was scheduled,
 * `false` when audio is unavailable (no Web Audio, locked context, or
 * an error during scheduling). Never throws.
 *
 * Safe to call from any frequency — every call creates a fresh oscillator
 * + gain node, so overlapping calls layer correctly. We don't debounce
 * here because the call site already gates on "active thread is not the
 * one the message arrived in".
 */
export async function playNotificationChime(opts: ChimeOptions = {}): Promise<boolean> {
  const c = getContext();
  if (!c) return false;
  const running = await ensureRunning(c);
  if (!running) return false;

  const freq = opts.frequency ?? 800;
  const duration = opts.durationSec ?? 0.14;
  const peak = opts.peakGain ?? 0.22;
  const now = c.currentTime;

  try {
    const osc = c.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    const gain = c.createGain();
    // Linear attack (4 ms) → exponential decay. setValueAtTime at 0
    // is required before linearRampToValueAtTime starts; otherwise
    // some browsers treat the initial value as ambient and the ramp
    // is inaudible.
    gain.gain.setValueAtTime(0, now);
    gain.gain.linearRampToValueAtTime(peak, now + 0.004);
    // exponentialRampToValueAtTime can't target zero — use a small
    // floor and then snap to zero so we don't leave a faint hum.
    gain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
    gain.gain.setValueAtTime(0, now + duration + 0.005);

    osc.connect(gain).connect(c.destination);
    osc.start(now);
    osc.stop(now + duration + 0.02);
    return true;
  } catch {
    return false;
  }
}

/**
 * Test-only — reset the cached context so unit tests can re-stub
 * window.AudioContext between cases. Calling from production code is
 * harmless (lazy re-creation on next play) but not useful.
 */
export function __resetChimeContextForTests(): void {
  ctx = null;
  resolveCtx = null;
}

/**
 * Pure gating predicate for the App.svelte realtime arm. Split out so
 * the chime decision is testable without booting Svelte.
 *
 * Returns true ONLY when:
 *  - the frame is a freshly-created INBOUND message,
 *  - the operator is NOT viewing that conversation right now (inbox
 *    counts as "not viewing" so the chime fires there too),
 *  - the local sound preference is enabled.
 *
 * Direction is read from `payload.direction` — both the inbound
 * consumer and the outbound send service include it explicitly. If a
 * future emit site forgets to set it, we conservatively skip the
 * chime; a missing chime is preferable to a chime on the operator's
 * own outbound send.
 */
export interface ChimeFrame {
  eventName?: string;
  payload?: { direction?: string; conversationId?: string } | null;
}

export function shouldPlayChime(input: {
  frame: ChimeFrame;
  activeConversationId: string | null;
  soundEnabled: boolean;
}): boolean {
  if (!input.soundEnabled) return false;
  if (input.frame.eventName !== 'message.created') return false;
  const payload = input.frame.payload;
  if (!payload || payload.direction !== 'INBOUND') return false;
  if (typeof payload.conversationId !== 'string') return false;
  if (input.activeConversationId === payload.conversationId) return false;
  return true;
}
